import { SelectChangeEvent } from "@mui/material"
import { User } from "graphql/types"
import { useStoreActions, useStoreState } from "hooks/storeHooks"
import { enqueueSnackbar } from "notistack"
import { useCallback, useEffect, useMemo, useState } from "react"
import { emptyPerson } from "store/store"
import { useLocalStorage } from "utils/useLocalStorage"
import { ALL_EMPLOYEES_VALUE, ERROR_MESSAGES } from "./constants"
import { filterUserProjects, isValidProject } from "./ProjectViewsUtils"

/**
 * Custom hook for dashboard data fetching and state management
 * Consolidates common logic between desktop and mobile dashboard components
 */
export const useDashboard = (options?: { includeMobileState?: boolean }) => {
  // Store state
  const projects = useStoreState((state) => state.projects)
  const employees = useStoreState((state) => state.employees)
  const leads = useStoreState((state) => state.leads)
  const customers = useStoreState((state) => state.customers)
  
  // Store actions
  const { fetchProjects, fetchEmployees, fetchLeads, fetchCustomers } = useStoreActions(
    (actions) => actions
  )
  
  // Local state
  const [user] = useLocalStorage<User>("user", emptyPerson)
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>("")
  const [selectedProjectId, setSelectedProjectId] = useState<string>("")
  const [isLoading, setIsLoading] = useState<boolean>(true)

  // Memoized error handlers
  const handleFetchError = useCallback((error: Error) => {
    console.error("Dashboard: Failed to fetch projects:", error)
    enqueueSnackbar(`${ERROR_MESSAGES.FETCH_ERROR} ${error.message}`, {
      variant: "error",
      autoHideDuration: 6000,
    })
  }, [])

  const handleFetchEmployeesError = useCallback((error: Error) => {
    console.error("Dashboard: Failed to fetch employees:", error)
    enqueueSnackbar(`${ERROR_MESSAGES.FETCH_EMPLOYEES_ERROR} ${error.message}`, {
      variant: "error",
      autoHideDuration: 6000,
    })
  }, [])

  // Fetch data on mount with improved error handling
  useEffect(() => {
    const abortController = new AbortController()

    const fetchData = async () => {
      try {
        setIsLoading(true)
        await Promise.allSettled([
          fetchProjects({ fresh: true }),
          fetchEmployees({ fresh: false }),
          fetchLeads({ fresh: false }),
          fetchCustomers({ fresh: false }),
        ]).then((results) => {
          results.forEach((result, index) => {
            if (result.status === "rejected") {
              const errorHandler = index === 0 ? handleFetchError : handleFetchEmployeesError
              errorHandler(result.reason)
            }
          })
        })
      } catch (error) {
        if (!abortController.signal.aborted) {
          console.error("Dashboard: Unexpected error during data fetch:", error)
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false)
        }
      }
    }

    fetchData()

    return () => {
      abortController.abort()
    }
  }, [fetchProjects, fetchEmployees, fetchLeads, fetchCustomers, handleFetchError, handleFetchEmployeesError])

  // Set initial selected employee to current user
  useEffect(() => {
    if (user.id && !selectedEmployeeId) {
      setSelectedEmployeeId(user.id)
    }
  }, [user.id, selectedEmployeeId])

  // Optimized project filtering with validation
  const filteredProjects = useMemo(() => {
    if (!selectedEmployeeId) {
      return []
    }

    if (selectedEmployeeId === ALL_EMPLOYEES_VALUE) {
      return projects.filter(isValidProject)
    }

    return filterUserProjects(projects, selectedEmployeeId)
  }, [projects, selectedEmployeeId])

  // Set initial selected project for mobile
  useEffect(() => {
    if (options?.includeMobileState && filteredProjects.length > 0 && !selectedProjectId) {
      setSelectedProjectId(filteredProjects[0].id)
    }
  }, [filteredProjects, selectedProjectId, options?.includeMobileState])

  // Memoized handlers
  const handleEmployeeChange = useCallback((event: SelectChangeEvent<string>) => {
    setSelectedEmployeeId(event.target.value)
  }, [])

  const handleProjectSelect = useCallback((projectId: string) => {
    setSelectedProjectId(projectId)
  }, [])

  return {
    // Data
    projects: filteredProjects,
    employees,
    leads,
    customers,
    user,
    
    // State
    selectedEmployeeId,
    selectedProjectId,
    isLoading,
    
    // Actions
    handleEmployeeChange,
    handleProjectSelect,
    
    // Derived state
    hasProjects: filteredProjects.length > 0,
  }
}